import { FC, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  styled,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import { InputField } from 'src/components/input-field';
import { BankDetails } from 'src/interfaces/new-channel-partner/channelPartner';
import { ResponseCodes } from 'src/enums/responseCodes';
import toast from 'react-hot-toast';
import { ifscApi } from 'src/api/ifsc';
import { SUPPORTED_FORMATS } from 'src/enums/common';
import { s3FileApi } from 'src/api/s3file';

interface EditBankInfoProps {
  open: boolean;
  onClose: () => void;
  bankDetails?: BankDetails;
  updateBankDetails?: (data: BankDetails) => Promise<boolean>;
}

export const EditBankInfo: FC<EditBankInfoProps> = (props) => {
  const { open, onClose, bankDetails, updateBankDetails } = props;
  const [fileToUpload, setFileToUpload] = useState(null);
  const [ifscCheck, setIFSCCheck] = useState<boolean>(false);
  const stringRegExp = /^[A-Za-z ]+$/;

  // this method maintain the selection when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      onClose();
    }
  };

  const isNumeric = (num) => /^\d+$/.test(num);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      cheque: {
        name: bankDetails?.cheque?.name || '',
        type: bankDetails?.cheque?.type || '',
        key: bankDetails?.cheque?.key || ''
      },
      accountHolderName: bankDetails?.accountHolderName || '',
      accountNumber: bankDetails?.accountNumber || '',
      confirmAccountNumber: bankDetails?.accountNumber || '',
      ifscCode: bankDetails?.ifscCode ? bankDetails?.ifscCode : '',
      name: bankDetails?.name || '',
      city: bankDetails?.city || '',
      branch: bankDetails?.branch || '',
      accountType: bankDetails?.accountType || '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      cheque: Yup.mixed()
        .test(
          'fileFormat',
          'File Format is not supported',
          (value) => value && SUPPORTED_FORMATS.includes(value.type)
        ).required('File is required'),
      accountHolderName: Yup.string().max(255)
        .required('Account Holder Name is Required').matches(new RegExp(stringRegExp),
          'Only alphabets and spaces are Allowed'),
      accountNumber: Yup.string()
        .test(
          'accountNumber',
          'Enter Valid Number',
          (value) => isNumeric(value)
        )
        .min(9, 'Must be 9-18 digits')
        .max(18, 'Must be 9-18 digits')
        .required('Account Number is Required'),
      confirmAccountNumber: Yup.string()
        .equals([Yup.ref('accountNumber')], 'Bank Account number must match')
        .required('Confirm Account Number is Required'),
      accountType: Yup.string().required('Account type is Required'),
      branch: Yup.string().required('Branch is Required'),
      name: Yup.string().required('Bank Name is Required'),
      city: Yup.string().required('City is Required'),
      ifscCode: Yup.string()
        .required('IFSC Code is Required')
        .test(
          'IFSC Code',
          'IFSC Code must be of length 11',
          (value) => value?.length === 11
        )
        .matches(
          /^[A-Za-z]{4}[a-zA-Z0-9]{7}$/,
          'First 4 characters must be alphabets and last 7 characters must be numbers'
        ),
    }),

    onSubmit: async (values, helpers) => {
      const editBankDetails: BankDetails = {
        name: values.name,
        ifscCode: values.ifscCode,
        accountHolderName: values.accountHolderName,
        accountNumber: values.accountNumber,
        accountType: values.accountType,
        branch: values.branch,
        cheque: values.cheque,
        city: values.city,
      };
      if (values.cheque?.key) {
        closeDialog('', '');
        updateBankDetails(editBankDetails);
        helpers.resetForm();
      } else {
        const uploadResponse = await s3FileApi
          .uploadMultiple('channelPartner', '', [fileToUpload]);
        if (uploadResponse.status === ResponseCodes.OK) {
          const fileToUpdate = { ...formik.values.cheque };
          fileToUpdate.key = uploadResponse.data[0].key;
          formik.values.cheque.key = uploadResponse.data[0].key;
          await formik.setFieldValue('cheque', fileToUpdate);
          const resultStatus = await updateBankDetails(editBankDetails);
          if (resultStatus) {
            closeDialog('', '');
          }
        } else {
          toast.error('Failed To Upload Cheque');
        }
      }
    }
  });

  const FileName = styled('p')(() => ({
    fontSize: 14,
    marginTop: 0,
    marginBottom: 0,
  }));

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    setFileToUpload(uploadedFile);
    const fileInfo = {
      type: uploadedFile?.type,
      name: uploadedFile?.name,
      key: null
    };
    formik.setFieldValue('cheque', fileInfo);
  };

  const handleScanCopyDelete = async () => {
    formik.setFieldValue('cheque', null);
  };

  const handleIFSCCheck = async (event) => {
    const enteredIFSC = event.target.value?.trim();
    await formik.setFieldValue('ifscCode', enteredIFSC);
    try {
      if (enteredIFSC.length === 11) {
        await formik.setFieldTouched('ifscCode', true);
        const result = await ifscApi.getBankDetailsByIfscCode(enteredIFSC);
        if (result.statusCode === ResponseCodes.OK) {
          setIFSCCheck(false);
          formik.handleChange(event);
          formik.setFieldValue('branch', result.responseData.BRANCH);
          formik.setFieldValue('name', result.responseData.BANK);
          formik.setFieldValue('city', result.responseData.CITY);
          await formik.setFieldError('ifscCode', undefined);
        } else {
          await formik.setFieldError('ifscCode', result?.responseData);
          setIFSCCheck(true);
          formik.setFieldValue('branch', '');
          formik.setFieldValue('name', '');
          formik.setFieldValue('city', '');
          formik.setFieldTouched('ifscCode', false);
        }
      } else {
        formik.handleChange(event);
        await formik.setFieldTouched('ifscCode', false);
        formik.setFieldValue('branch', '');
        formik.setFieldValue('name', '');
        formik.setFieldValue('city', '');
      }
    } catch (err) {
      console.error(err.message);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle
        sx={{ justifyContent: 'flex-end' }}
      >
        {bankDetails?.ifscCode ? 'Edit Bank Details' : 'Add Bank Details'}
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => {
            formik.resetForm();
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ mb: -2, justifyItems: 'center' }}
      >
        <Grid
          container
          spacing={1}
        >
          <Grid
            container
            mt={1}
            spacing={1}
          >
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.ifscCode
              && formik.errors.ifscCode)}
                fullWidth
                helperText={formik.touched.ifscCode
              && formik.errors.ifscCode}
                placeholder="Please enter your IFSC"
                name="ifscCode"
                required
                label="IFSC Code"
                onBlur={formik.handleBlur}
                onChange={handleIFSCCheck}
                sx={{ mb: 1 }}
                value={formik.values.ifscCode}
              />

              { ifscCheck && !formik.errors.ifscCode && (
              <Grid
                sx={{
                  ml: '13px',
                  mt: '-9px'
                }}
              >
                <Typography
                  lineHeight={0.5}
                  sx={{
                    fontWeight: '1'
                  }}
                  variant="caption"
                  color="red"
                >
                  Invalid IFSC Code
                </Typography>
              </Grid>
              )}
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.branch && formik.errors.branch)}
                helperText={formik.touched.branch && formik.errors.branch}
                placeholder="Branch Name"
                name="branch"
                required
                fullWidth
                disabled
                label="Branch Name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.branch}
              />

            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                placeholder="Enter Bank Name"
                name="name"
                required
                fullWidth
                disabled
                label="Bank Name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.name}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >
              <InputField
                error={Boolean(formik.touched.city && formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                placeholder="Enter City"
                name="city"
                required
                label="City Name"
                fullWidth
                disabled
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.city}
              />
            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.accountHolderName
              && formik.errors.accountHolderName)}
                fullWidth
                helperText={formik.touched.accountHolderName
              && formik.errors.accountHolderName}
                placeholder="Account Holder Name"
                name="accountHolderName"
                required
                label="Account holder name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.accountHolderName}
              />

            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.accountNumber
              && formik.errors.accountNumber)}
                fullWidth
                helperText={formik.touched.accountNumber
              && formik.errors.accountNumber}
                placeholder="Bank Account Number"
                name="accountNumber"
                required
                type="Password"
                label="Bank Account Number"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.accountNumber}
              />

            </Grid>
            <Grid
              item
              md={3}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.confirmAccountNumber
              && formik.errors.confirmAccountNumber)}
                fullWidth
                helperText={formik.touched.confirmAccountNumber
              && formik.errors.confirmAccountNumber}
                placeholder="Confirm Account Number"
                name="confirmAccountNumber"
                required
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                label="Confirm Account"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                sx={{ mb: 1 }}
                value={formik.values.confirmAccountNumber}
              />

            </Grid>
            <Grid
              mr={-1}
              item
              md={3}
              sm={12}
              xs={12}
            >

              <InputField
                error={Boolean(formik.touched.accountType && formik.errors.accountType)}
                helperText={formik.touched.accountType && formik.errors.accountType}
                placeholder="Select Account Type"
                name="accountType"
                required
                label="Account Type"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                select
                fullWidth
                sx={{ mb: 1 }}
                value={formik.values.accountType}
              >
                <MenuItem
                  key="accountType"
                  value=""
                />
                {['Current Account', 'Saving Account'].map((accountType) => (
                  <MenuItem
                    key={accountType}
                    value={accountType}
                  >
                    {accountType}
                  </MenuItem>
                ))}
              </InputField>

            </Grid>
            <Grid
              item
              md={12}
              sm={12}
              xs={12}
            >
              <Typography
                variant="button"
                display="inline-flex"
                flexDirection="row"
              >
                Cheque
                <Typography
                  flexDirection="inherit"
                  color="red"
                >
                  {' '}
                  *
                </Typography>
              </Typography>
              {formik.values.cheque?.name ? (
                <Stack
                  direction="row"
                  ml={3}
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                >
                  <FileName>{formik.values.cheque?.name}</FileName>
                  <IconButton
                    aria-label="delete"
                    size="large"
                    color="error"
                    onClick={handleScanCopyDelete}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Stack>
              ) : (
                <label
                  htmlFor="scanDocUpload"
                >
                  <input
                    type="file"
                    accept=".png, .jpg, .jpeg , application/pdf"
                    name="scanCopyFile"
                    id="scanDocUpload"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <Button
                    sx={{
                      ml: 3
                    }}
                    color="primary"
                    variant="contained"
                    component="span"
                  >
                    Upload
                  </Button>
                </label>
              )}
            </Grid>
            <Grid
              item
              md={12}
              sm={12}
              xs={12}
            >
              <Box>
                <Alert severity="warning">
                  <AlertTitle>Note</AlertTitle>
                  Scan of Cancelled cheque with your name or Latest bank statement that shows:
                  <br />
                  1. Your Bank account number.
                  <br />
                  2. IFSC and MICR Code.
                  <br />
                  <strong>(In Case the cheque does not include your name </strong>
                  <strong>and there is no mention of IFSC & MICR code, </strong>
                  <strong>your aplication will be rejected)</strong>
                </Alert>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          ml={-1}
          spacing={1}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'right'
        }}
      >
        <Button
          color="primary"
          onClick={(e) => {
            formik.resetForm();
            closeDialog(e, '');
          }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={(!(formik.isValid && formik.dirty) || formik.isSubmitting) || ifscCheck}
          color="primary"
          onClick={() => { formik.handleSubmit(); }}
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditBankInfo.defaultProps = {
  open: false,
};

EditBankInfo.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  // @ts-ignore
  bankDetails: PropTypes.object,
  updateBankDetails: PropTypes.func
};
